/**
 * Tabbed UI for configuring an org template's schema (modules and schedules).
 *
 * Manages local editing state cloned from props, tracks dirty state,
 * and persists changes via the useUpdateOrgTemplateSchema hook. IDs are
 * validated live (format + duplicate derived docx tags) with inline errors
 * passed to the editors; Save is disabled while any ID error exists.
 * Uses brand-compliant typography and colours (text-emerald-700 for success).
 */

import { useState, useCallback } from 'react';
import { Button, TabButton } from '../common';
import ModuleEditor from './ModuleEditor';
import ScheduleEditor from './ScheduleEditor';
import SchemaTagAuditPanel from './SchemaTagAuditPanel';
import { useUpdateOrgTemplateSchema } from '../../hooks/useOrgTemplates';
import { validateOrgTemplateSchema } from '../../utils/orgSchemaValidation';
import type { OrgTemplateSchema, OrgModule, OrgSchedule } from '../../types/orgTemplate';

interface SchemaConfiguratorProps {
  templateId: string;
  schema?: OrgTemplateSchema;
  /** Tags extracted from the uploaded docx; enables the document tag audit. */
  bodyTags?: string[];
}

const EMPTY_SCHEMA: OrgTemplateSchema = { modules: [], schedules: [] };

type TabId = 'modules' | 'schedules';

export default function SchemaConfigurator({ templateId, schema, bodyTags }: SchemaConfiguratorProps) {
  const [activeTab, setActiveTab] = useState<TabId>('modules');
  const [localSchema, setLocalSchema] = useState<OrgTemplateSchema>(schema || EMPTY_SCHEMA);
  const [isDirty, setIsDirty] = useState(false);
  const updateSchema = useUpdateOrgTemplateSchema(templateId);

  // Pattern 0: re-seed local schema during render when the persisted schema
  // prop changes (e.g. after a save + query invalidation). Guard on a stable
  // string key to avoid re-seeding on identity-equal objects.
  const schemaKey = JSON.stringify(schema);
  const [prevSchemaKey, setPrevSchemaKey] = useState(schemaKey);
  if (schemaKey !== prevSchemaKey) {
    setPrevSchemaKey(schemaKey);
    setLocalSchema(schema || EMPTY_SCHEMA);
    setIsDirty(false);
  }

  const handleModulesChange = useCallback((modules: OrgModule[]) => {
    setLocalSchema((prev) => ({ ...prev, modules }));
    setIsDirty(true);
  }, []);

  const handleSchedulesChange = useCallback((schedules: OrgSchedule[]) => {
    setLocalSchema((prev) => ({ ...prev, schedules }));
    setIsDirty(true);
  }, []);

  const validation = validateOrgTemplateSchema(localSchema);

  const handleSave = async () => {
    if (validation.hasErrors) return;
    await updateSchema.mutateAsync(localSchema);
    setIsDirty(false);
  };

  const tabs: { id: TabId; label: string; count: number }[] = [
    { id: 'modules', label: 'Modules', count: localSchema.modules.length },
    { id: 'schedules', label: 'Schedules', count: localSchema.schedules.length },
  ];

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex items-center justify-between">
        <div role="tablist" className="flex border-b border-slate-200 dark:border-slate-700">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              panelId={`schema-${tab.id}-panel`}
              badge={tab.count > 0 ? tab.count : undefined}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </TabButton>
          ))}
        </div>

        {isDirty && (
          <Button
            onClick={handleSave}
            loading={updateSchema.isPending}
            disabled={validation.hasErrors}
            size="sm"
          >
            Save Schema
          </Button>
        )}
      </div>

      {isDirty && validation.hasErrors && (
        <p className="text-body-sm text-red-600 dark:text-red-400">
          Fix the ID errors above before saving.
        </p>
      )}

      {/* Tab content */}
      {activeTab === 'modules' && (
        <div role="tabpanel" id="schema-modules-panel" aria-labelledby="schema-modules-panel-tab">
          <ModuleEditor
            modules={localSchema.modules}
            onChange={handleModulesChange}
            errors={{
              moduleIdErrors: validation.moduleIdErrors,
              orderFormIdErrors: validation.orderFormIdErrors,
            }}
          />
        </div>
      )}
      {activeTab === 'schedules' && (
        <div role="tabpanel" id="schema-schedules-panel" aria-labelledby="schema-schedules-panel-tab">
          <ScheduleEditor
            schedules={localSchema.schedules}
            modules={localSchema.modules}
            onChange={handleSchedulesChange}
            errors={validation.scheduleIdErrors}
          />
        </div>
      )}

      {bodyTags && bodyTags.length > 0 && (
        <SchemaTagAuditPanel bodyTags={bodyTags} schema={localSchema} />
      )}

      {updateSchema.isError && (
        <p className="text-body-sm text-red-600 dark:text-red-400">
          Failed to save schema: {updateSchema.error?.message}
        </p>
      )}

      {updateSchema.isSuccess && !isDirty && (
        <p className="text-body-sm text-emerald-700 dark:text-emerald-400">Schema saved successfully.</p>
      )}
    </div>
  );
}
